import {setEffectParameterValue} from 'actions/index.js';
import KeyScaling from './KeyScaling.js';
import {recordAutomation} from 'operations/actions.js';
import Automations from './Automations.js';
import {isNumber} from 'utils/IsNumber.js';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import {uuid} from 'uuidv4';
import ParameterChangeEvent from 'session/ParameterChangeEvent.js';
import {convertPatternToValue} from './convertPatternToValue.js';
import {convertValue} from './convertPatternToValue.js';
import ParameterMod from './ParameterMod.js';
import {RECORD} from 'constants/automation-modes.js';
import {moveKey, deleteKey} from 'utils/objectMover.js';
import {LFO, ENVELOPE} from 'constants/mod-types.js';
import Tone from 'tone';
import BaseParameter from './BaseParameter.js';
import Lockable from './Lockable.js';
import Automatable from './Automatable.js';
import Palettable from './Palettable.js';
import MutableParam from './MutableParam.js';
import Modulatable from './Modulatable.js';
import Savable from './Savable.js';
import Param2D from './2DParam.js';
import PLockable from './PLockable.js';
import {compose} from 'utils/compose.js';

const context = Tone.Master.context;

/**
 * Every parameter in the program can be represented with this
 * Allows setting the parameter from 0 to 1 even if it takes
 * a different range of values, which are defined by the
 * min and max fields
 */

class AbstractParameter extends compose(
    Lockable,
    PLockable,
    Automatable,
    Palettable,
    MutableParam,
    Modulatable,
    Savable,
    Param2D,
)(BaseParameter) {

    get documentation() {
        if (this.config.documentation) {
            let doc = Array.isArray(this.config.documentation) ?
                this.config.documentation : [this.config.documentation];
            return [
                ... doc,
                "Click to view modulators for this parameter. Double click for automation"
            ];
        }
        return "Click to view modulators for this parameter. Double click for automation";
    }

    disposed() {
        return this._wasDisposed;
    }

    dispose(disposeAudioParam=true) {
        if (this.disposed() || this.config.doNotDispose) {
            return;
        }
        let sequencerEngine = this.sequencerEngine || window.sequencerEngine;
        if (sequencerEngine) {
            if (sequencerEngine.idToParameter) {
                delete sequencerEngine.idToParameter[this.id];
            } 
        } 

        this.disconnectFromAllModulators();
        this.unlockedAutomation.dispose();
        this.unlockedAutomation = undefined;

        for (let i in this.patternToAutomation) {
            this.patternToAutomation[i].dispose();
        }
        this.patternToAutomation = undefined;
        this.patternToValue = undefined;

        if (disposeAudioParam) {
            if (this.audioParam && this.audioParam.input) {
                this.audioParam.dispose();
            }
            
            if (this.audioParams) {
                this.audioParams
                    .filter(p => p.input)
                    .forEach(p => p.dispose());
            }

            if (this._audioParams) {
                this._audioParams
                    .filter(p => p.input)
                    .forEach(p => p.dispose());
            }
        }

        this.context = undefined;
        this.audioParam = undefined;
        this.audioParams = undefined;
        this.object = undefined;
        this.id = undefined;
        this.name = undefined;
        this.prettyName = undefined;
        this.envelopes = undefined;
        this.lfos = undefined;
        this.unlockedValue = undefined;

        if (this.depthParameter) {
            this.depthParameter.dispose();
            this.depthParameter = undefined;
        }
        this._wasDisposed = true;
    }

    get state() {
        return this.store.getState();
    }

    get sequencerEngine() {
        if (this.track) {
            return this.track.sequencerEngine;
        }

        if (this.object && this.object.track) {
            return this.object.track.sequencerEngine;
        }

        if (this.context && this.context.effect) {
            return this.context.effect.sequencerEngine;
        }

        if (this.context && this.context.track) {
            return this.context.track.sequencerEngine;
        }

        if (this.context && this.context.instrumentRack) {
            return this.context.instrumentRack.sequencerEngine;
        }

        if (this.context && this.context.instrument) {
            return this.context.instrument.sequencerEngine;
        }

        if (this.context && this.context.lfoTrack) {
            return this.context.lfoTrack.sequencerEngine;
        }

        if (this.context && this.context.envelopeTrack) {
            return this.context.envelopeTrack.sequencerEngine;
        }

        return undefined;
    }

    isValidValue(value) {
        return !isNaN(value) &&
            value >= this.min &&
            value <= this.max;
    }

    setContext(context) {
        this.context = context;
        if (this.context.track !== undefined) {
            this.track = this.context.track;
            if (this.track.sequencerEngine) {
                this.track.sequencerEngine.idToParameter[this.id] = this;
            }
        } else if (this.context.lfoTrack) {
            if (this.context.lfoTrack.sequencerEngine) {
                this.context.lfoTrack.sequencerEngine.idToParameter[this.id] = this;
            }
        } else if (this.context.envelopeTrack) {
            if (this.context.envelopeTrack.sequencerEngine) {
                this.context.envelopeTrack.sequencerEngine.idToParameter[this.id] = this;
            }
        } else if (this.context.group) {
            if (this.context.group.sequencerEngine) {
                this.context.group.sequencerEngine.idToParameter[this.id] = this;
            }
        } else if (this.context.send) {
            if (this.context.send.sequencerEngine) {
                this.context.send.sequencerEngine.idToParameter[this.id] = this;
            }
        }
    }

    setStore(store) {
        if (store !== undefined) {
            this.store = store;
            this.unlockedAutomation.store = store;
            this.mixAutomation.store = store;
            this.automation.updateRedux();
        }
        let o = {};
        o[this.id] =  this.get(true);
        return o;
    }

    setMixSession(mixSession) {
        this.mixSession = mixSession;
    }

    setTrack(track) {
        // the track obj (if it exists) contains information on patterns
        this.track = track;
    }

    copyFrom(parameter, sequencerEngine, isShallowCopy=false) {
        if (isShallowCopy) {
            this.value = parameter.value;
        } else {
            this.disconnectFromAllModulators();
            this.fromJson(parameter.getJson(), sequencerEngine);
            if (parameter.isMapped) {
                parameter.isMapped.replaceParameter(this);
            }
            this.isMapped = parameter.isMapped;
            this.set(parameter.value, true);
        }
    }

    get mix() {
        return this.context && this.context.subSequence ?
            this.context.subSequence.mix : undefined;
    }

    getNumber(force) {
        return this.get(force);
    }

    get(force=false, depth=0) {
        if (this.disposed()) {
            return 0;
        }
        if (!force && this.shouldUseGetAtStep()) {
            let x = this.getAtStep(this.currentStep);
            return x;
        }

        if (this.isConnectedToAnyModulator(false) && !force) {
            let v = this.getMeterValue(depth);
            v = (parseFloat(this.value) + parseFloat(v));
            if (v >= this.max) {
                return this.max;
            }
            return v;
        }

        if (this.audioParam !== undefined && this.meter !== undefined) {
            if (this.value === null || this.value === undefined ||
                isNaN(this.value)) {
                return this.value;
            }
        } else {
            if (this.value === null) {
                return null;
            }
            return this.value;
        }
    }

    get trackStep() {
        if (this.context === undefined || this.context.track === undefined) {
            return 0;
        }
        return this.context.track.sequencerEngine.trackStep;
    }

    initDepth() {
        this.depthParameter = new AbstractParameter(
            'depth', this, () => undefined, {
                min: 0,
                max: 1,
                defaultValue: this._depth || 1,
                automationRate: A_RATE
            },
        );
        if (this.context) {
            this.depthParameter.setContext(this.context);
        }
        this.depthParameter.setStore(this.store);
    }
}

class IntegerParameter extends AbstractParameter {
    constructor(name, object, func, min, max, units, val, automatable=true, setDefault=true) {
        super(name, object, func, min, max, units, val, automatable, setDefault);
    }

    normalize(x) {
        return Math.round(this.min + (this.max - this.min) * x);
    }

    set(x, automation, bulk) {
        if (!isNumber(x)) {
            return;
        }
        if (x === null) {
            return super.set(null, automation, bulk);
        } else {
            return super.set(Math.round(x), automation, bulk);
        }
    }
}

class ValueParameter extends AbstractParameter {
    constructor(name, object, min, max, units, value) {
        super(name, object, (o, x) => o.value = x, min, max, units, value, true);
    }
}

class PercentageValueParameter extends ValueParameter {
    constructor(name, object) {
        super(name, object, 0, 1, '%', 0); 
    }
}

const isNullOrEmpty = (x) => x === undefined;

export {AbstractParameter, IntegerParameter, ValueParameter, PercentageValueParameter}; 
